import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  Search,
  Eye,
  Download,
  MapPin,
  Briefcase,
  CheckCircle,
  XCircle,
  FileText,
  User,
  Shield,
  File,
  X,
  RefreshCw
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import documentVerificationService from '../../../api/services/documentVerificationService';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatDate } from '../../../utils/stringUtils';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import WarningPopup from '../../../components/WarningPopup';
import candidateService from '../../../api/services/candidateService';
import toast from 'react-hot-toast';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';


const DocumentVerification = () => {
  const { user } = useAuth();
  const { hasPermission, canUpdate } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const formatDisplayDate = (value) => {
    if (!value) return 'N/A';
    try {
      return formatDate(value, { includeTime: false });
    } catch (error) {
      return 'N/A';
    }
  };
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [filterDocumentType, setFilterDocumentType] = useState(searchParams.get('documentType') || 'all');
  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || ''
  });
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get('limit')) || 10);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [documentToReject, setDocumentToReject] = useState(null);
  const [bulkRejectMode, setBulkRejectMode] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  // API state
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Role-based access control functions
  const canExportData = () => {
    return hasPermission('onboarding', 'read');
  };



  // Handler functions
  const handleExportDocuments = async (selectedOnly = false) => {
    try {

      let documentsToDownload = [];

      if (selectedOnly && selectedDocuments.length > 0) {
        // Get only selected documents
        documentsToDownload = selectedDocuments.map(({ candidateId, documentId }) => {
          const candidate = candidates.find(c => c.id === candidateId);
          const document = candidate?.documents.find(d => d.id === documentId);
          return document ? {
            ...document,
            candidateName: candidate.name,
            candidateEmail: candidate.email
          } : null;
        }).filter(Boolean);

      } else {
        // Get all documents from all candidates
        documentsToDownload = candidates.flatMap(candidate =>
          candidate.documents.map(doc => ({
            ...doc,
            candidateName: candidate.name,
            candidateEmail: candidate.email
          }))
        );

      }

      if (documentsToDownload.length === 0) {
        setError(selectedOnly ? 'No documents selected for download' : 'No documents available for download');
        return;
      }

      // Download each document
      for (let i = 0; i < documentsToDownload.length; i++) {
        const doc = documentsToDownload[i];
        try {
          const result = await documentVerificationService.downloadDocument(doc.id, {
            fileName: doc.fileName || doc.name,
            fileType: doc.fileType,
          });
          if (!result.success) {
            // If it requires login, stop the bulk download and refresh
            if (result.requiresLogin) {
              window.location.reload();
              return;
            }
          } else {
          }

          // Add small delay between downloads to prevent overwhelming the server
          if (i < documentsToDownload.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } catch (error) {
        }
      }


      // If this was for selected documents, clear the selection
      if (selectedOnly) {
        setSelectedDocuments([]);
        setShowBulkActions(false);
      }
    } catch (error) {
      setError('Failed to download some documents. Please try again.');
    }
  };

  const filteredCandidates = candidates.filter(candidate => {
    const matchesSearch = candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (candidate.jobTitle && candidate.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()));


    const matchesDocumentType = filterDocumentType === 'all' ||
      candidate.documents.some(doc => doc.type === filterDocumentType);


    return matchesSearch && matchesDocumentType;
  });

  const baseExportFilters = useMemo(() => {
    const f = {};
    const trimmed = searchTerm?.trim();
    if (trimmed) f.search = trimmed;
    if (filterStatus !== 'all') f.status = filterStatus;
    if (filterDocumentType !== 'all') f.documentType = filterDocumentType;
    if (dateRange.startDate) f.startDate = dateRange.startDate;
    if (dateRange.endDate) f.endDate = dateRange.endDate;
    return f;
  }, [
    searchTerm,
    filterStatus,
    filterDocumentType,
    dateRange.startDate,
    dateRange.endDate,
  ]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'document-verification',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? page,
            limit: overrideLimit ?? pageSize,
            sortBy: 'candidateName',
            sortOrder: 'asc',
            selectedIds,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Export request failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackExt = format === 'xlsx' ? 'xlsx' : format;
      const fallbackName = `document-verification-export.${fallbackExt}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, page, pageSize]
  );

  const handleDocumentSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows);
      if (!ids.length) {
        toast.error('No selected candidates to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} candidate(s) exported.`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleDocumentScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        if (scope === 'currentPage' && !filteredCandidates.length) {
          toast.error('No candidates to export on the current page.');
          return;
        }

        if (scope === 'filtered' && totalItems === 0) {
          toast.error('No candidates match the current filters.');
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          return;
        }

        toast.success('Export downloaded.');
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed. Please try again.');
      } finally {
        setExportLoading(false);
      }
    },
    [filteredCandidates.length, totalItems, baseExportFilters, requestBackendExport]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const handleBulkDeleteCandidate = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteModal(true);
  };

  const confirmDeleteCandidate = async () => {
    setIsDeleting(true);
    try {
      if (isBulkDelete) {
        const idsToDelete = Array.from(selectedRows);
        let successCount = 0;
        let errorCount = 0;

        for (const id of idsToDelete) {
          try {
            const result = await candidateService.deleteCandidate(id, true);
            // Service might return different shapes, check success
            if (result && (result.responseCode === 200 || result.success || result.status === 'success')) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (error) {
            errorCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} candidates deleted successfully`);
          setSelectedRows(new Set());
          loadDocuments();
        }
        if (errorCount > 0) {
          toast.error(`Failed to delete ${errorCount} candidates`);
        }
      }
      setShowDeleteModal(false);
      setIsBulkDelete(false);
    } catch (error) {
      toast.error('An error occurred while deleting');
    } finally {
      setIsDeleting(false);
    }
  };

  const clearError = () => {
    setError(null);
  };



  const handleViewDocument = (candidate, document) => {
    setSelectedDocument({ candidate, document });
    setShowDocumentModal(true);
  };

  const handleRejectDocument = (candidateId, documentId) => {
    setDocumentToReject({ candidateId, documentId });
    setBulkRejectMode(false);
    setShowRejectModal(true);
  };

  const handleBulkReject = () => {
    setDocumentToReject(null);
    setBulkRejectMode(true);
    setShowRejectModal(true);
  };

  const confirmRejection = async () => {
    if (rejectNotes.trim()) {
      try {
        if (bulkRejectMode) {
          // Process all selected documents
          const promises = selectedDocuments.map(({ candidateId, documentId }) =>
            handleDocumentAction(candidateId, documentId, 'rejected', rejectNotes)
          );

          // Wait for all rejections to complete
          await Promise.all(promises);

          // Clear selections and close modal
          setSelectedDocuments([]);
          setShowBulkActions(false);
        } else if (documentToReject) {
          await handleDocumentAction(documentToReject.candidateId, documentToReject.documentId, 'rejected', rejectNotes);
        }

        // Close modal and reset state
        setShowRejectModal(false);
        setRejectNotes('');
        setDocumentToReject(null);
        setBulkRejectMode(false);

      } catch (error) {
        setError('Failed to reject document(s). Please try again.');
      }
    }
  };

  // Load documents from API
  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await documentVerificationService.getDocuments({
        status: filterStatus,
        documentType: filterDocumentType,
        search: searchTerm,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page,
        limit: pageSize
      });

      if (result.success) {

        // Group documents by candidate
        const candidateMap = new Map();

        result.data.forEach(doc => {
          if (!candidateMap.has(doc.candidateId)) {
            candidateMap.set(doc.candidateId, {
              id: doc.candidateId,
              candidateId: doc.candidateId,
              name: doc.candidateName,
              email: doc.candidateEmail,
              phone: doc.candidatePhone,
              jobTitle: doc.jobTitle,
              department: doc.department,
              appliedDate: doc.createdAt,
              documents: [],
              overallStatus: 'pending',
              completionPercentage: 0
            });
          }

          const candidate = candidateMap.get(doc.candidateId);
          candidate.documents.push({
            id: doc.id,
            type: doc.documentType,
            name: doc.documentName,
            fileName: doc.documentName,
            uploadedDate: doc.uploadedDate,
            status: doc.status,
            verifiedBy: doc.verifiedBy,
            verifiedDate: doc.verifiedAt,
            verifiedAt: doc.verifiedAt,
            notes: doc.verificationNotes,
            fileSize: doc.fileSize,
            fileType: doc.fileType,
            fileUrl: doc.fileUrl
          });

          // Set isDocumentVerified from the document data
          if (doc.isDocumentVerified !== undefined) {
            candidate.isDocumentVerified = doc.isDocumentVerified;
          }
        });

        // Calculate overall status and completion percentage for each candidate
        const candidatesArray = Array.from(candidateMap.values()).map(candidate => {
          const totalDocs = candidate.documents.length;
          const approvedDocs = candidate.documents.filter(doc => doc.status === 'approved').length;
          const rejectedDocs = candidate.documents.filter(doc => doc.status === 'rejected').length;
          const pendingDocs = candidate.documents.filter(doc => doc.status === 'pending').length;

          let overallStatus = 'pending';
          if (totalDocs === 0) {
            overallStatus = 'pending';
          } else if (approvedDocs === totalDocs) {
            overallStatus = 'completed';
          } else if (approvedDocs > 0 || rejectedDocs > 0) {
            overallStatus = 'in_progress';
          } else if (pendingDocs === totalDocs) {
            overallStatus = 'pending';
          }

          const completionPercentage = totalDocs > 0 ? Math.round((approvedDocs / totalDocs) * 100) : 0;

          return {
            ...candidate,
            overallStatus,
            completionPercentage
          };
        });

        setCandidates(candidatesArray);
        // Update pagination from server if present
        if (result.pagination) {
          setTotalPages(result.pagination.totalPages || 1);
          setTotalItems(result.pagination.totalItems || 0);
          if (result.pagination.currentPage && result.pagination.currentPage !== page) {
            setPage(result.pagination.currentPage);
          }
        } else if (typeof result.total === 'number') {
          // Fallback for older response shapes
          setTotalItems(result.total);
          setTotalPages(Math.max(Math.ceil(result.total / pageSize), 1));
        } else {
          // No pagination meta; infer from page payload
          setTotalItems(candidatesArray.reduce((sum, c) => sum + (c.documents?.length || 0), 0));
          setTotalPages(1);
        }
      } else {
        setError(result.error || 'Failed to load documents');
      }
    } catch (err) {
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  // Load documents on component mount and when filters change
  useEffect(() => {
    loadDocuments();
    setSelectedRows(new Set());
  }, [filterStatus, filterDocumentType, searchTerm, page, pageSize, dateRange.startDate, dateRange.endDate]);

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = ['search', 'status', 'documentType', 'startDate', 'endDate', 'page', 'limit'];
      managedKeys.forEach(key => params.delete(key));

      if (searchTerm) params.set('search', searchTerm);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (filterDocumentType && filterDocumentType !== 'all') params.set('documentType', filterDocumentType);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (page && page !== 1) params.set('page', String(page));
      if (pageSize && pageSize !== 10) params.set('limit', String(pageSize));

      return params;
    }, { replace: true });
  }, [searchTerm, filterStatus, filterDocumentType, dateRange, page, pageSize, setSearchParams]);

  const documentTypes = [
    { id: 'aadhar_card', name: 'Aadhar Card', required: true },
    { id: 'pan_card', name: 'PAN Card', required: true },
    { id: 'passport', name: 'Passport', required: false },
    { id: 'degree_certificate', name: 'Degree Certificate', required: true },
    { id: 'experience_certificate', name: 'Experience Certificate', required: true },
    { id: 'bank_details', name: 'Bank Account Details', required: true },
    { id: 'address_proof', name: 'Address Proof', required: true },
    { id: 'other', name: 'Other Document', required: false }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'pending': return 'Pending';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      default: return status;
    }
  };

  const getDocumentTypeIcon = (type) => {
    switch (type) {
      case 'aadhar_card': return Shield;
      case 'pan_card': return FileText;
      case 'passport': return FileText;
      case 'degree_certificate': return FileText;
      case 'experience_certificate': return Briefcase;
      case 'bank_details': return FileText;
      case 'address_proof': return MapPin;
      case 'other': return File;
      default: return File;
    }
  };

  const getDocumentTypeColor = (type) => {
    switch (type) {
      case 'aadhar_card': return 'text-blue-600';
      case 'pan_card': return 'text-green-600';
      case 'passport': return 'text-purple-600';
      case 'degree_certificate': return 'text-orange-600';
      case 'experience_certificate': return 'text-indigo-600';
      case 'bank_details': return 'text-emerald-600';
      case 'address_proof': return 'text-pink-600';
      case 'other': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  const handleDocumentAction = async (candidateId, documentId, action, notes = '') => {
    try {
      // Convert UI action to API action
      const apiAction = action === 'approved' ? 'approve' : 'reject';

      const result = await documentVerificationService.verifyDocument(documentId, apiAction, notes);

      if (result.success) {
        // Update local state
        setCandidates(prev => prev.map(candidate => {
          if (candidate.id === candidateId) {
            const updatedDocuments = candidate.documents.map(doc => {
              if (doc.id === documentId) {
                return {
                  ...doc,
                  status: action,
                  verifiedBy: user?.fullName || user?.email || 'Current User',
                  verifiedDate: new Date().toISOString().split('T')[0],
                  verifiedAt: new Date().toISOString(),
                  notes: notes
                };
              }
              return doc;
            });

            // Update overall status based on document statuses
            const approvedCount = updatedDocuments.filter(doc => doc.status === 'approved').length;
            const totalCount = updatedDocuments.length;
            const rejectedCount = updatedDocuments.filter(doc => doc.status === 'rejected').length;
            const pendingCount = updatedDocuments.filter(doc => doc.status === 'pending').length;

            const completionPercentage = Math.round((approvedCount / totalCount) * 100);

            let overallStatus = 'pending';
            if (totalCount === 0) {
              overallStatus = 'pending';
            } else if (approvedCount === totalCount) {
              overallStatus = 'completed';
            } else if (approvedCount > 0 || rejectedCount > 0) {
              overallStatus = 'in_progress';
            } else if (pendingCount === totalCount) {
              overallStatus = 'pending';
            }

            // Update isDocumentVerified based on all documents being approved
            const isDocumentVerified = totalCount > 0 && approvedCount === totalCount;

            return {
              ...candidate,
              documents: updatedDocuments,
              completionPercentage,
              overallStatus,
              isDocumentVerified
            };
          }
          return candidate;
        }));

        return { success: true };
      } else {
        setError(result.error || 'Failed to verify document');
        return { success: false, error: result.error };
      }
    } catch (err) {
      setError('Failed to verify document');
      return { success: false, error: err.message };
    }
  };

  const handleBulkAction = async (action) => {
    try {
      // Convert UI action to API action
      const apiAction = action === 'approved' ? 'approve' : 'reject';

      const promises = selectedDocuments.map(({ candidateId, documentId }) =>
        handleDocumentAction(candidateId, documentId, action)
      );

      await Promise.all(promises);
      setSelectedDocuments([]);
      setShowBulkActions(false);
    } catch (err) {
      setError('Failed to perform bulk action');
    }
  };

  const toggleDocumentSelection = (candidateId, documentId) => {
    setSelectedDocuments(prev => {
      if (prev.some(sel => sel.candidateId === candidateId && sel.documentId === documentId)) {
        return prev.filter(sel => !(sel.candidateId === candidateId && sel.documentId === documentId));
      } else {
        return [...prev, { candidateId, documentId }];
      }
    });
  };

  const DocumentCard = ({ candidate, document, hideCheckbox = false }) => {
    const IconComponent = getDocumentTypeIcon(document.type);
    const iconColor = getDocumentTypeColor(document.type);
    const isSelected = selectedDocuments.some(sel =>
      sel.candidateId === candidate.id && sel.documentId === document.id
    );
    const uploadedDateDisplay = formatDisplayDate(document.uploadedDate);

    // Get document type name and capitalize it
    const documentTypeName = documentTypes.find(type => type.id === document.type)?.name ||
      document.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    return (
      <div
        className={`bg-white rounded-lg border-2 p-4 transition-all h-full flex flex-col shadow-sm hover:shadow-md ${!hideCheckbox ? 'cursor-pointer' : ''} ${isSelected ? 'border-agrosaf-green bg-green-50' : 'border-gray-200 hover:border-gray-300'
          }`}
        onClick={(e) => {
          // Only toggle selection if clicking on the card itself, not on buttons or checkbox
          const isButton = e.target.tagName === 'BUTTON' || e.target.closest('button') !== null;
          const isInput = e.target.tagName === 'INPUT';
          const isIcon = e.target.tagName === 'svg' || e.target.closest('svg') !== null;

          if (!isButton && !isInput && !isIcon && !hideCheckbox) {
            toggleDocumentSelection(candidate.id, document.id);
          }
        }}
      >
        {/* Header Section */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {!hideCheckbox && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleDocumentSelection(candidate.id, document.id);
                }}
                className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green mt-1 flex-shrink-0"
              />
            )}
            <div className="flex items-center space-x-2 flex-shrink-0">
              <IconComponent className={`h-5 w-5 ${iconColor}`} />
              {document.type === 'aadhar_card' && (
                <Shield className="h-4 w-4 text-blue-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 text-sm leading-tight mb-1 capitalize">
                {documentTypeName}
              </h4>
              <p className="text-xs text-gray-500 truncate">{document.fileName}</p>
            </div>
          </div>
          <span className={`px-2 py-1 text-xs font-semibold rounded-full flex-shrink-0 ml-2 whitespace-nowrap capitalize ${getStatusColor(document.status)}`}>
            {getStatusLabel(document.status)}
          </span>
        </div>

        {/* Document Details Section */}
        <div className="space-y-3 text-sm text-gray-600 mb-4 flex-1">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-700">Uploaded:</span>
              <span className="text-xs font-semibold">{uploadedDateDisplay}</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-700">File Size:</span>
              <span className="text-xs font-semibold">{document.fileSize}</span>
            </div>
            {document.verifiedBy && (
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-700">Verified by:</span>
                <span className="text-xs font-semibold truncate max-w-24">{document.verifiedBy}</span>
              </div>
            )}
          </div>
        </div>

        {/* Notes Section */}
        {document.notes && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-gray-700 mb-4">
            <div className="flex items-start space-x-2">
              <FileText className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-yellow-800">Rejection Reason</span>
                <p className="text-yellow-700 mt-1 truncate" title={document.notes}>
                  {document.notes.length > 50 ? `${document.notes.substring(0, 50)}...` : document.notes}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons Section */}
        <div className="mt-auto">
          <div className="flex flex-col space-y-2">
            <PermissionGate permission="onboarding" action="read">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleViewDocument(candidate, document);
                }}
                className="w-full px-3 py-2 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center font-medium border border-blue-200"
              >
                <Eye className="h-3 w-3 mr-1" />
                View Details
              </button>
            </PermissionGate>
            {document.status === 'pending' && (
              <div className="flex space-x-2">
                <PermissionGate permission="onboarding" action="update">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDocumentAction(candidate.id, document.id, 'approved');
                    }}
                    className="flex-1 px-3 py-2 text-xs bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors flex items-center justify-center font-medium border border-green-200"
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Approve
                  </button>
                </PermissionGate>
                <PermissionGate permission="onboarding" action="update">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRejectDocument(candidate.id, document.id);
                    }}
                    className="flex-1 px-3 py-2 text-xs bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center font-medium border border-red-200"
                  >
                    <XCircle className="h-3 w-3 mr-1" />
                    Reject
                  </button>
                </PermissionGate>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Document Verification</h1>
          <p className="text-gray-600 mt-1">Verify and manage candidate documents for onboarding</p>
        </div>
        <div className="flex items-center space-x-4">
          {canExportData() && (
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={<Download className={`h-4 w-4 ${exportLoading ? 'animate-spin' : ''}`} />}
                onClick={handleExportClick}
                disabled={exportLoading}
                className={selectedRows.size > 0 ? 'bg-blue-50 border-blue-200 text-blue-600' : ''}
              >
                {exportLoading ? 'Exporting...' : (selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export')}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleDocumentSelectedFormatExport}
              />
            </div>
          )}
          {selectedRows.size > 0 && (
            <PermissionGate permission="onboarding" action="delete">
              <Button
                variant="primary"
                onClick={handleBulkDeleteCandidate}
                className="bg-red-600 hover:bg-red-700 border-red-600 flex items-center gap-2"
              >
                Delete ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          {selectedDocuments.length > 0 && (
            <Button
              variant="primary"
              onClick={() => setShowBulkActions(true)}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
            >
              Bulk Actions ({selectedDocuments.length})
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search candidates..."
              value={searchTerm}
              onChange={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setSearchTerm(value);
              }}
              onKeyPress={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              maxLength={100}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            />
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Document Type Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterDocumentType}
              onChange={(e) => setFilterDocumentType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            >
              <option value="all">All Document Types</option>
              {documentTypes.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          {/* Date Range Filter */}
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(range) => {
                setDateRange(range);
                setPage(1);
              }}
            />
          </div>

          {/* Clear Filters */}
          {(searchTerm || filterStatus !== "all" || filterDocumentType !== "all" || dateRange.startDate || dateRange.endDate) && (
            <div className="relative flex-shrink-0">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={() => {
                  setSearchTerm("");
                  setFilterStatus("all");
                  setFilterDocumentType("all");
                  setDateRange({ startDate: '', endDate: '' });
                }}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <XCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
            <button
              onClick={clearError}
              className="ml-3 text-red-400 hover:text-red-600"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Selection Control */}
      {!loading && filteredCandidates.length > 0 && (
        <div className="flex items-center space-x-2 px-1">
          <input
            type="checkbox"
            checked={filteredCandidates.length > 0 && filteredCandidates.every(c => selectedRows.has(c.id))}
            onChange={() => {
              const currentPageIds = filteredCandidates.map(c => c.id);
              const selectedOnPage = currentPageIds.filter(id => selectedRows.has(id));

              if (selectedOnPage.length > 0) {
                setSelectedRows(new Set());
              } else {
                const newSelected = new Set(selectedRows);
                currentPageIds.forEach(id => newSelected.add(id));
                setSelectedRows(newSelected);
              }
            }}
            className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green"
          />       <span className="text-sm font-medium text-gray-700">Select All Candidates (Current Page)</span>
          {selectedRows.size > 0 && (
            <span className="text-sm text-gray-500 italic ml-2">
              ({selectedRows.size} selected)
            </span>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="p-8 text-center">
          <RefreshCw className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Documents...</h3>
          <p className="text-gray-600">Please wait while we fetch the documents.</p>
        </div>
      ) : (
        <>
          {/* Candidates List */}
          <div className="space-y-6">
            {filteredCandidates.map(candidate => (
              <div key={candidate.id} className={`bg-white rounded-lg shadow-sm border p-6 transition-all ${selectedRows.has(candidate.id) ? 'border-agrosaf-green bg-green-50 shadow-md' : 'border-gray-200'}`}>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 mb-6 gap-4">
                  <div className="flex items-center space-x-4 min-w-0">
                    <div className="flex items-center space-x-3 flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={selectedRows.has(candidate.id)}
                        onChange={() => {
                          const newSelectedRows = new Set(selectedRows);
                          if (newSelectedRows.has(candidate.id)) {
                            newSelectedRows.delete(candidate.id);
                          } else {
                            newSelectedRows.add(candidate.id);
                          }
                          setSelectedRows(newSelectedRows);
                        }}
                        className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green h-4 w-4"
                      />
                      <div className="w-12 h-12 bg-gradient-to-br from-agrosaf-green to-agrosaf-blue rounded-full flex items-center justify-center">
                        <User className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 break-words capitalize">{candidate.name}</h3>
                      <p className="text-sm text-gray-600 break-words capitalize">{candidate.jobTitle}</p>
                      <p className="text-sm text-gray-500 break-all">{candidate.email}</p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500">Progress:</span>
                          <span className="text-sm font-medium">{candidate.completionPercentage}%</span>
                        </div>
                        <div className="w-24 bg-gray-200 rounded-full h-2 mt-1">
                          <div
                            className="bg-agrosaf-green h-2 rounded-full transition-all duration-300"
                            style={{ width: `${candidate.completionPercentage}%` }}
                          ></div>
                        </div>
                      </div>
                      <span className={`px-3 py-1 text-sm font-semibold rounded-full flex-nowrap capitalize ${getStatusColor(candidate.overallStatus)}`}>
                        {getStatusLabel(candidate.overallStatus)}
                      </span>
                    </div>

                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-stretch">
                  {candidate.documents.map(document => (
                    <DocumentCard key={document.id} candidate={candidate} document={document} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Footer */}
          {totalItems > pageSize && (
            <div className="px-6 py-4 border-t border-gray-200 bg-white rounded-lg">
              <PaginationControls
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                limit={pageSize}
                hasNextPage={page < totalPages}
                hasPrevPage={page > 1}
                onPageChange={setPage}
                onLimitChange={(l) => { setPageSize(Number(l)); setPage(1); }}
              />
            </div>
          )}

          {/* Empty State */}
          {filteredCandidates.length === 0 && (
            <div className="text-center py-12">
              <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No candidates found</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || filterStatus !== 'all' || filterDocumentType !== 'all' || dateRange.startDate || dateRange.endDate
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No candidates have uploaded documents for verification yet.'
                }
              </p>
            </div>
          )}
        </>
      )}

      {/* Bulk Actions Modal */}
      {showBulkActions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Bulk Actions</h3>
              <p className="text-sm text-gray-600 mb-6">
                You have selected {selectedDocuments.length} document{selectedDocuments.length !== 1 ? 's' : ''}. Choose an action:
              </p>
              <div className="space-y-3">
                <PermissionGate permission="onboarding" action="update">
                  <button
                    onClick={() => handleBulkAction('approved')}
                    className="w-full px-4 py-2 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                  >
                    <CheckCircle className="h-4 w-4 inline mr-2" />
                    Approve All
                  </button>
                </PermissionGate>
                <PermissionGate permission="onboarding" action="update">
                  <button
                    onClick={handleBulkReject}
                    className="w-full px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                  >
                    <XCircle className="h-4 w-4 inline mr-2" />
                    Reject All
                  </button>
                </PermissionGate>
                <PermissionGate permission="onboarding" action="read">
                  <button
                    onClick={() => handleExportDocuments(true)}
                    className="w-full px-4 py-2 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                  >
                    <Download className="h-4 w-4 inline mr-2" />
                    Download Selected Documents
                  </button>
                </PermissionGate>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="secondary"
                  onClick={() => setShowBulkActions(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}

      {/* Document View Modal */}
      {showDocumentModal && selectedDocument && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 !mt-0">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Document Details</h3>
                <button
                  onClick={() => {
                    setShowDocumentModal(false);
                    setSelectedDocument(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Candidate Name</label>
                    <p className="mt-1 text-sm text-gray-900 break-words capitalize">
                      {selectedDocument.candidate.name}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900 break-all">
                      {selectedDocument.candidate.email}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Document Name</label>
                    <p className="mt-1 text-sm text-gray-900 break-words capitalize">
                      {selectedDocument.document.name.replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Document Type</label>
                    <p className="mt-1 text-sm text-gray-900 break-words capitalize">
                      {documentTypes.find(type => type.id === selectedDocument.document.type)?.name ||
                        selectedDocument.document.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${selectedDocument.document.status === 'approved' ? 'bg-green-100 text-green-800' :
                      selectedDocument.document.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                      {selectedDocument.document.status}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Uploaded Date</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatDisplayDate(selectedDocument.document.uploadedDate)}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">File Information</label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{selectedDocument.document.fileName}</p>
                        <p className="text-sm text-gray-500">{selectedDocument.document.fileSize}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedDocument.document.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Rejection Reason</label>
                    <p className="mt-1 text-sm text-gray-900 break-words line-clamp-3" title={selectedDocument.document.notes}>
                      {selectedDocument.document.notes}
                    </p>
                  </div>
                )}

                {selectedDocument.document.verifiedBy && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Verified By</label>
                      <p className="mt-1 text-sm text-gray-900 break-words">
                        {selectedDocument.document.verifiedBy}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Verified At</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formatDisplayDate(selectedDocument.document.verifiedAt)}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDocumentModal(false);
                    setSelectedDocument(null);
                  }}
                >
                  Close
                </Button>
                <Button
                  variant="secondary"
                  icon={<Download className="h-4 w-4" />}
                  onClick={async () => {
                    try {
                      const result = await documentVerificationService.downloadDocument(
                        selectedDocument.document.id,
                        {
                          fileName: selectedDocument.document.fileName || selectedDocument.document.name,
                          fileType: selectedDocument.document.fileType,
                        }
                      );
                      if (!result.success) {
                        // If it requires login, don't show error, just refresh
                        if (result.requiresLogin) {
                          // Silently refresh the page to trigger re-authentication
                          window.location.reload();
                          return;
                        }
                        setError(result.error || 'Failed to download document');
                      }
                    } catch (err) {
                      // Don't show the technical error message
                      if (err.message && !err.message.includes('user belonging to this token')) {
                        setError('Failed to download document. Please try again.');
                      }
                    }
                  }}
                >
                  Download
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 !mt-0">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {bulkRejectMode ? 'Reject All Documents' : 'Reject Document'}
                </h3>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectNotes('');
                    setDocumentToReject(null);
                    setBulkRejectMode(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="rejectNotes" className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Rejection *
                  </label>
                  <textarea
                    id="rejectNotes"
                    value={rejectNotes}
                    onChange={(e) => {
                      // Only allow alphanumeric, spaces, and commas
                      const sanitized = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '');
                      setRejectNotes(sanitized.slice(0, 150));
                    }}
                    onKeyPress={(e) => {
                      // Only allow alphanumeric, spaces, and commas
                      if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    rows={4}
                    maxLength={500}
                    placeholder="Please provide a reason for rejecting this document..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectNotes('');
                    setDocumentToReject(null);
                    setBulkRejectMode(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="secondary"
                  className="text-red-600 hover:text-red-800"
                  icon={<XCircle className="h-4 w-4" />}
                  onClick={confirmRejection}
                  disabled={!rejectNotes.trim()}
                >
                  {bulkRejectMode ? 'Reject All' : 'Reject Document'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WarningPopup for Deletion */}
      <WarningPopup
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteCandidate}
        title="Delete Candidates"
        message={`Are you sure you want to delete ${selectedRows.size} selected candidates? This action cannot be undone.`}
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Document Verification Data"
        showDateRange
        scopedFormatExport={{
          onExport: handleDocumentScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
    </div>
  );
};

export default DocumentVerification;
